import React, { createContext, useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext'; // Import useAuth
import { API_BASE_URL } from '../config/api';
// import { fetchNotionProjects, fetchNotionClients } from '../services/notionService'; // Add notion service import
import { cachedRequest, clearCacheEntry } from '../utils/requestCache';
import { useLocation } from 'react-router-dom'; // Import useLocation
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Initialize dayjs plugins (provider-level utilities rely on timezone math)
dayjs.extend(utc);
dayjs.extend(timezone);

const TeamspaceContext = createContext();

// --- API Fetching Utilities ---

const TEAMSPACE_API_URL = `${API_BASE_URL}teamspaces/`;

/** If cached options are older than this, useOptionsForTeamspace triggers a background revalidate so new projects/members/tags appear without a full refresh. */
const OPTIONS_CACHE_STALE_MS = 5 * 60 * 1000; // 5 minutes

const fetchMembersForAutocomplete = async (teamspaceId) => {
  console.log(`Fetching members for teamspace ID: ${teamspaceId}`);
  const apiUrl = `${TEAMSPACE_API_URL}${teamspaceId}/teamspace_members/?include_inactive=true`;
  try {
    const response = await cachedRequest(axios.get, apiUrl, {}, 300000); // 5 minutes cache
    const members = response.data.map((row) => ({
      label: row.member_username || row.email_alt, // Use username, fallback to email_alt for display
      id: row.id, // Use the actual TeamspaceMemberRole ID
      email: row.email, // Keep email for reference
      rate: row.rate, // Include the rate from TeamspaceMemberRole
      username: row.member_username, // Keep username for reference
      timezone: row.timezone, // Include timezone from TeamspaceMemberRole
      role: row.role, // Role ID from TeamspaceMemberRole (e.g. 'Staff', 'Lead')
      is_active: row.is_active !== false, // Include active status; default true if missing
    }));
    return members;
  } catch (error) {
    console.error("Failed to fetch members:", error);
    throw error;
  }
};

const fetchClientsForAutocomplete = async (teamspaceId, { forceRefresh = false } = {}) => {
  console.log(`Fetching clients for teamspace ID: ${teamspaceId}`);
  const apiUrl = `${TEAMSPACE_API_URL}${teamspaceId}/clients/`;
  try {
    let allClients = [];
    let currentUrl = apiUrl;

    // Fetch all pages of clients with caching
    while (currentUrl) {
      // Live-refresh path: drop the stale entry so cachedRequest re-fetches and
      // re-stores a fresh copy (later normal reads then get the fresh data too).
      if (forceRefresh) clearCacheEntry('GET', currentUrl);
      const response = await cachedRequest(axios.get, currentUrl, {}, 300000); // 5 minutes cache
      const data = response.data;
      
      // Handle paginated response
      if (data.results) {
        // Paginated response
        allClients = [...allClients, ...data.results];
        currentUrl = data.next; // Get next page URL
      } else if (Array.isArray(data)) {
        // Non-paginated response (fallback)
        allClients = [...allClients, ...data];
        currentUrl = null;
      } else {
        // Unexpected format
        console.warn('Unexpected response format from clients API:', data);
        break;
      }
    }
    
    // Return full client objects (not just label/id) for use in both autocomplete and full data storage
    return allClients;
  } catch (error) {
    console.error("Failed to fetch clients:", error);
    throw error;
  }
};

const fetchProjectsForAutocomplete = async (teamspaceId, returnFullData = false, { forceRefresh = false } = {}) => {
  console.log(`Fetching projects for teamspace ID: ${teamspaceId}`);
  const apiUrl = `${TEAMSPACE_API_URL}${teamspaceId}/projects/`;
  try {
    let allProjects = [];
    let currentUrl = apiUrl;

    // Fetch all pages of projects with caching
    while (currentUrl) {
      // Live-refresh path: drop the stale entry so cachedRequest re-fetches and
      // re-stores a fresh copy (later normal reads then get the fresh data too).
      if (forceRefresh) clearCacheEntry('GET', currentUrl);
      const response = await cachedRequest(axios.get, currentUrl, {}, 300000); // 5 minutes cache
      const data = response.data;
      
      // Handle paginated response
      if (data.results) {
        // Paginated response
        allProjects = [...allProjects, ...data.results];
        currentUrl = data.next; // Get next page URL
      } else if (Array.isArray(data)) {
        // Non-paginated response (fallback)
        allProjects = [...allProjects, ...data];
        currentUrl = null;
      } else {
        // Unexpected format
        console.warn('Unexpected response format from projects API:', data);
        break;
      }
    }
    
    // Return full data if requested, otherwise return simplified format for autocomplete
    if (returnFullData) {
      return allProjects;
    }
    
    const projects = allProjects.map((row) => ({
      label: row.full_project_name || row.name, // Use full_project_name if available
      id: row.id,
      client: row.client, // preserve FK client for frontend filtering
      full_project_name: row.full_project_name, // preserve full name for reference
    }));
    return projects;
  } catch (error) {
    console.error("Failed to fetch projects:", error);
    throw error;
  }
};

export const fetchTimeEntriesForAutocomplete = async (teamspaceId, startDate = null, endDate = null, memberUsername = null) => {
  console.log(`Fetching time entries for teamspace ID: ${teamspaceId}`);
  let apiUrl = `${TEAMSPACE_API_URL}${teamspaceId}/time_entries/`;
  
  // Add date range parameters if provided
  if (startDate && endDate) {
    const queryObj = {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      // Reduce pagination calls for large teamspaces (DRF-style param; safe if backend ignores it)
      page_size: 200,
    };

    // Optional filter: fetch only a single member (if backend supports it)
    if (memberUsername) {
      queryObj.member_username = memberUsername;
    }

    const queryParams = new URLSearchParams(queryObj).toString();
    apiUrl = `${apiUrl}?${queryParams}`;
  }
  try {
    let allTimeEntries = [];
    let currentUrl = apiUrl;
    
    // Fetch all pages of time entries with caching
    while (currentUrl) {
      const response = await cachedRequest(axios.get, currentUrl, {}, 300000); // 5 minutes cache
      const data = response.data;
      
      // Handle paginated response
      if (data.results) {
        // Paginated response
        allTimeEntries = [...allTimeEntries, ...data.results];
        currentUrl = data.next; // Get next page URL
      } else if (Array.isArray(data)) {
        // Non-paginated response (fallback)
        allTimeEntries = [...allTimeEntries, ...data];
        currentUrl = null;
      } else {
        // Unexpected format
        console.warn('Unexpected response format from time entries API:', data);
        break;
      }
    }
    
    return allTimeEntries;
  } catch (error) {
    console.error("Failed to fetch time entries:", error);
    throw error;
  }
};

const fetchTagsForAutocomplete = async (teamspaceId) => {
  console.log(`Fetching tags for teamspace ID: ${teamspaceId}`);
  const apiUrl = `${TEAMSPACE_API_URL}${teamspaceId}/tags/`;
  try {
    const response = await cachedRequest(axios.get, apiUrl, {}, 300000); // 5 minutes cache
    const tags = response.data.map((row) => ({
      label: row.name,
      id: row.id,
    }));
    return tags;
  } catch (error) {
    console.error("Failed to fetch tags:", error);
    throw error;
  }
};

const fetchCategoriesForAutocomplete = async (teamspaceId) => {
  const apiUrl = `${TEAMSPACE_API_URL}${teamspaceId}/time_entry_categories/`;
  try {
    let allCategories = [];
    let currentUrl = apiUrl;
    while (currentUrl) {
      const response = await cachedRequest(axios.get, currentUrl, {}, 300000); // 5 minutes cache
      const data = response.data;
      if (data.results) {
        allCategories = [...allCategories, ...data.results];
        currentUrl = data.next;
      } else if (Array.isArray(data)) {
        allCategories = [...allCategories, ...data];
        currentUrl = null;
      } else {
        break;
      }
    }
    return allCategories.map((row) => ({
      label: row.name ? row.name.toUpperCase() : row.name,
      name: row.name,
      id: row.id,
      isCategory: true,
    }));
  } catch (error) {
    console.warn("Time entry categories endpoint not available:", error);
    return [];
  }
};

// --- Lookup Map Creator ---

const createLookupMap = (items) => {
  if (!items || !Array.isArray(items)) return {};
  return items.reduce((map, item) => {
    const key = String(item.id);
    map[key] = item.label;
    return map;
  }, {});
};

const createRateLookupMap = (members) => {
  if (!members || !Array.isArray(members)) return {};
  return members.reduce((map, member) => {
    map[member.email] = member.rate;  // Use email as key for rate lookup
    return map;
  }, {});
};

// --- Notion Data Fetching Utilities ---

// const fetchNotionData = async (teamspaceId, teamspaceName) => {
//   console.log(`🔄 Starting Notion data fetch for teamspace: ${teamspaceId} (${teamspaceName})`);
  
//   try {
//     console.log(`📊 Fetching Notion projects for teamspace: ${teamspaceId} (${teamspaceName})`);
//     const notionProjects = await fetchNotionProjects(teamspaceId, teamspaceName);
//     console.log(`✅ Successfully fetched ${notionProjects.length} Notion projects`);

//     console.log(`📊 Fetching Notion clients for teamspace: ${teamspaceId} (${teamspaceName})`);
//     const notionClients = await fetchNotionClients(teamspaceId, teamspaceName);
//     console.log(`✅ Successfully fetched ${notionClients.length} Notion clients`);

//     return {
//       notionProjects,
//       notionClients,
//     };
//   } catch (error) {
//     console.error(`❌ Error fetching Notion data for teamspace ${teamspaceId} (${teamspaceName}):`, error);
//     // Return empty arrays on error to prevent breaking the app
//     return {
//       notionProjects: [],
//       notionClients: [],
//     };
//   }
// };

export const TeamspaceProvider = ({ children }) => {
  const [teamspaces, setTeamspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamspaceId, setSelectedTeamspaceId] = useState(null);
  const [projectLookup, setProjectLookup] = useState({});
  const [projects, setProjects] = useState([]); // raw projects with client FK
  const [members, setMembers] = useState([]); // raw members with email
  const [memberLookup, setMemberLookup] = useState({});
  const [clientLookup, setClientLookup] = useState({});
  const [tagLookup, setTagLookup] = useState({});
  const [teamspaceMemberRateLookup, setTeamspaceMemberRateLookup] = useState({}); // New state for member rates
  const [lookupLoading, setLookupLoading] = useState(false);
  const [teamspaceIdToNameMap, setTeamspaceIdToNameMap] = useState({});
  // Initialize userPreferences as empty object instead of null
  // This allows timezone to be calculated immediately (using device timezone as fallback)
  const [userPreferences, setUserPreferences] = useState({});
  const [userPreferencesLoaded, setUserPreferencesLoaded] = useState(false);
  const [clients, setClients] = useState([]); // New state for raw clients data
  const [timeEntries, setTimeEntries] = useState([]); // Filtered time entries for current teamspace (for compatibility)
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(false); // Loading state for time entries refresh
  const [tags, setTags] = useState([]); // New state for raw tags data
  // const [notionProjects, setNotionProjects] = useState([]); // New state for Notion projects
  // const [notionClients, setNotionClients] = useState([]); // New state for Notion clients
  const [dataRefreshTrigger, setDataRefreshTrigger] = useState(0); // Add this

  /** Per-teamspace cache for form options (projects, members, tags, categories). Used by time entry forms so they don't refetch on every open. Refreshed when header teamspace changes or via refetchOptionsForTeamspace. */
  const [optionsByTeamspace, setOptionsByTeamspace] = useState({});
  const optionsFetchInFlightRef = useRef(new Set());

  // Use ref to prevent duplicate concurrent fetches
  const fetchingRef = useRef(false);
  // Use ref to track latest timeEntries for optimistic updates
  const timeEntriesRef = useRef([]);

  // Global loading tracker for time entry mutations (create/update/delete).
  // Exposed via context so pages and forms can show a full-page loading overlay
  // and block user interaction while a mutation is in-flight.
  const [timeEntryMutationCount, setTimeEntryMutationCount] = useState(0);

  const startTimeEntryMutation = useCallback(() => {
    setTimeEntryMutationCount((count) => count + 1);
  }, []);

  const endTimeEntryMutation = useCallback(() => {
    setTimeEntryMutationCount((count) => Math.max(0, count - 1));
  }, []);

  const { isAuthenticated, loading: authLoading, user, role_dict } = useAuth(); // Get auth state and user

  // Add useLocation hook to access current URL
  const location = useLocation();

  // Get device timezone (calculated once, early)
  const deviceTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return 'UTC';
    }
  }, []);

  // Get current user's timezone from their teamspace member role
  const currentUserTimezone = useMemo(() => {
    if (!user || !members.length) return 'UTC';
    
    // Find the current user's member record
    const currentUserMember = members.find(member => 
      member.email === user.email || member.username === user.username
    );
    
    return currentUserMember?.timezone || 'UTC';
  }, [user, members]);

  // Calculate displayTimezone - this is the global timezone that should be used everywhere
  // This should match the header timezone display logic
  // ALWAYS returns a timezone (never null) - prioritizes showing timezone immediately
  const displayTimezone = useMemo(() => {
    // If use_device_timezone preference is explicitly true, always use device timezone
    if (userPreferences?.use_device_timezone === true) {
      return deviceTimezone;
    }
    
    // If use_device_timezone is explicitly false, try to use teamspace member role timezone
    if (userPreferences?.use_device_timezone === false) {
      // Use timezone from teamspace member role for selected teamspace
      if (selectedTeamspaceId && members && members.length > 0 && user) {
        const currentUserMember = members.find(member => 
          member.email === user.email || member.email === user.id || member.email === user.username
        );
        
        if (currentUserMember?.timezone) {
          return currentUserMember.timezone;
        }
      }
      
      // If members are still loading, return device timezone as temporary fallback
      // This ensures timezone always shows immediately in header
      if (members === null || (selectedTeamspaceId && members.length === 0)) {
        // Still loading members - use device timezone temporarily so header shows something
        return deviceTimezone;
      }
      
      // Members are loaded but no timezone found - use UTC as fallback
      return 'UTC';
    }
    
    // If userPreferences is not loaded yet (null), default to device timezone
    // This ensures timezone shows immediately even before preferences load
    return deviceTimezone;
  }, [userPreferences?.use_device_timezone, deviceTimezone, selectedTeamspaceId, members, user]);

  // Calculate period ranges in the ACTIVE display timezone (critical for correct billing periods)
  // Returns JS Date objects, but boundaries are computed as calendar days in `tz`.
  const calculatePeriodRanges = useMemo(() => {
    return (billingPeriod, tzOverride = null) => {
      const tz = tzOverride || displayTimezone || 'UTC';
      const now = tz === 'UTC' ? dayjs.utc() : dayjs().tz(tz);

      let currentStart, currentEnd, previousStart, previousEnd;

      if (billingPeriod === '25th') {
        if (now.date() > 25) {
          currentStart = now.date(26).startOf('day');
          currentEnd = now.add(1, 'month').date(25).endOf('day');
          previousStart = now.subtract(1, 'month').date(26).startOf('day');
          previousEnd = now.date(25).endOf('day');
        } else {
          currentStart = now.subtract(1, 'month').date(26).startOf('day');
          currentEnd = now.date(25).endOf('day');
          previousStart = now.subtract(2, 'month').date(26).startOf('day');
          previousEnd = now.subtract(1, 'month').date(25).endOf('day');
        }
      } else {
        // Default to monthly ('1st' behavior): 1st..last day of month in timezone
        currentStart = now.startOf('month').startOf('day');
        currentEnd = now.endOf('month').endOf('day');
        previousStart = now.subtract(1, 'month').startOf('month').startOf('day');
        previousEnd = now.subtract(1, 'month').endOf('month').endOf('day');
      }

      return {
        current: { start: currentStart.toDate(), end: currentEnd.toDate() },
        previous: { start: previousStart.toDate(), end: previousEnd.toDate() },
      };
    };
  }, [displayTimezone]);

  // Track if timezone is ready (userPreferences loaded and selectedTeamspaceId set)
  // Note: displayTimezone now always returns a value (never null), so this is mainly for tracking
  // when we have the final/preferred timezone vs temporary fallback
  const timezoneReady = useMemo(() => {
    // Timezone is ready when:
    // 1. User is authenticated
    // 2. UserPreferences is loaded (even if null/empty, it means we've checked)
    // 3. SelectedTeamspaceId is set
    // 4. Members are loaded (at least empty array means we've fetched)
    // displayTimezone is always available now (never null), so we don't need to check it
    const ready = isAuthenticated && 
                  userPreferences !== null && 
                  selectedTeamspaceId !== null && 
                  members !== null;
    
    if (ready) {
      console.log('🌍 [TeamspaceProvider] Timezone is ready:', {
        displayTimezone,
        use_device_timezone: userPreferences?.use_device_timezone,
        selectedTeamspaceId,
        membersCount: members?.length || 0,
        note: 'Timezone always available (may be temporary fallback until members load)'
      });
    }
    
    return ready;
  }, [isAuthenticated, userPreferences, selectedTeamspaceId, members, displayTimezone]);

  // Fetch user preferences - HIGH PRIORITY: Load immediately when authenticated
  // This is critical for timezone calculation, so we prioritize it
  const fetchUserPreferences = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      // If not authenticated, mark as loaded (empty object already set)
      setUserPreferencesLoaded(true);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}users/${user.id}/member_preferences/`);

      if (response.data.length > 0) {
        const prefs = response.data[0];
        setUserPreferences(prefs);
      } else {
        // No preferences found - keep empty object (defaults will be used)
        // This ensures timezone can still be calculated immediately
      }
      setUserPreferencesLoaded(true);
    } catch (error) {
      // On error, keep empty object so timezone can still use device timezone fallback
      // This ensures timezone always shows even if preferences fail to load
      console.warn('Failed to fetch user preferences, using defaults:', error);
      setUserPreferencesLoaded(true);
    }
  }, [isAuthenticated, user?.id]);

  // HIGH PRIORITY: Fetch user preferences immediately when authenticated
  // This is critical for timezone display in header
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchUserPreferences();
    } else if (!isAuthenticated) {
      // Not authenticated - mark as loaded immediately (empty object already set)
      setUserPreferencesLoaded(true);
    }
  }, [isAuthenticated, user, fetchUserPreferences]);
  
    // Effect to fetch teamspaces for the authenticated user
    useEffect(() => {
      // Only fetch teamspaces if authenticated and user data is available
      if (!isAuthenticated || !user || authLoading) {
        setTeamspaces([]);
        setSelectedTeamspaceId(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      const fetchTeamspaces = async () => {
        try {
          console.log('🔄 Fetching teamspaces...');
          const response = await axios.get(`${TEAMSPACE_API_URL}`);
          const userTeamspaceNames = Object.keys(role_dict);
          const filteredTeamspaces = response.data.filter(teamspace =>
            userTeamspaceNames.includes(teamspace.name)
          );

          setTeamspaces(filteredTeamspaces);

          if (filteredTeamspaces.length > 0) {
            // FIRST: Check if there's a teamspace ID in the URL
            const pathMatch = location.pathname.match(/^\/(\d+)\//);
            if (pathMatch) {
              const urlTeamspaceId = parseInt(pathMatch[1], 10);
              if (filteredTeamspaces.some(ts => ts.id === urlTeamspaceId)) {
                console.log(`🔄 Using teamspace from URL: ${urlTeamspaceId}`);
                setSelectedTeamspaceId(urlTeamspaceId);
                return;
              }
            }

            
            let defaultTeamspaceId = filteredTeamspaces[0].id; 

            
            if (!userPreferences) {
              setSelectedTeamspaceId(defaultTeamspaceId);
              return;
            }

            // Prioritize teamspace_name since that's what the API returns
            if (userPreferences?.teamspace_name) {
              const preferredTeamspace = filteredTeamspaces.find(ts => ts.name === userPreferences.teamspace_name);
              
              if (preferredTeamspace) {
                defaultTeamspaceId = preferredTeamspace.id;
              }
            }
            // Fallback to default_teamspace if it exists
            else if (userPreferences?.default_teamspace) {
              const preferredTeamspace = filteredTeamspaces.find(ts =>
                ts.id === userPreferences.default_teamspace.id ||
                ts.name === userPreferences.default_teamspace.name
              );

              if (preferredTeamspace) {
                defaultTeamspaceId = preferredTeamspace.id;
              }
            }
            // Legacy fallback
            else if (userPreferences?.teamspace_id) {
              const preferredTeamspace = filteredTeamspaces.find(ts => ts.name === userPreferences.teamspace_id);

              if (preferredTeamspace) {
                defaultTeamspaceId = preferredTeamspace.id;
              }
            }

            setSelectedTeamspaceId(defaultTeamspaceId);
          } else {
            setSelectedTeamspaceId(null);
          }
        } catch (error) {
          setTeamspaces([]);
          setSelectedTeamspaceId(null);
        } finally {
          setLoading(false);
        }
      };
      fetchTeamspaces();
    }, [isAuthenticated, user, authLoading, role_dict, userPreferences, location.pathname]); // Add location.pathname to dependencies
  
    
  useEffect(() => {
    if (teamspaces.length > 0) {
      const newMap = teamspaces.reduce((acc, teamspace) => {
        acc[teamspace.id] = teamspace.name;
        return acc;
      }, {});
      setTeamspaceIdToNameMap(newMap);
    }
  }, [teamspaces]);

  
  // Time entries are now fetched independently by pages that need them (e.g., CalendarPage)
  // No bulk fetching here to improve performance

  // In TeamspaceProvider.js
  useEffect(() => {
    // 1. Setup the "Kill Switch" for race conditions
    const controller = new AbortController();
  
    if (!selectedTeamspaceId) {
      // Clear data exactly as you did before
      setProjects([]);
      setMembers([]);
      setTags([]);
      setClients([]);
      setProjectLookup({});
      setTagLookup({});
      setClientLookup({});
      setMemberLookup({});
      return;
    }
  
    const fetchTeamspaceData = async () => {
      setLookupLoading(true);
      try {
        // 2. Fetch all data in parallel for better speed (including categories for form cache)
        const [projectsData, membersData, tagsData, clientsData, categoriesData] = await Promise.all([
          fetchProjectsForAutocomplete(selectedTeamspaceId, true),
          fetchMembersForAutocomplete(selectedTeamspaceId),
          fetchTagsForAutocomplete(selectedTeamspaceId),
          fetchClientsForAutocomplete(selectedTeamspaceId),
          fetchCategoriesForAutocomplete(selectedTeamspaceId),
        ]);
  
        // 3. THE GUARD: If the user switched teamspaces while we were waiting, 
        // STOP HERE. Do not update state with "stale" data.
        if (controller.signal.aborted) return;
  
        // 4. RESTORE YOUR EXACT LOOKUP LOGIC
        // Project Lookup: Stores the full_project_name or name string
        const projectLookupObj = {};
        projectsData.forEach(project => {
          projectLookupObj[project.id] = project.full_project_name || project.name || '';
        });
        
        // Member Lookup: Stores the .label string
        const memberLookupObj = {};
        membersData.forEach(member => {
          memberLookupObj[member.id] = member.label;
        });
  
        // Tag Lookup: Stores the .label or .name string
        const tagLookupObj = {};
        tagsData.forEach(tag => {
          tagLookupObj[tag.id] = tag.label || tag.name || '';
        });
  
        // Client Lookup: Stores the .code or .name string
        const clientLookupObj = {};
        clientsData.forEach(client => {
          clientLookupObj[client.id] = client.code || client.name || '';
        });
  
        // 5. Update State (using the exact same variable names as your original)
        setProjects(projectsData);
        setProjectLookup(projectLookupObj);
        
        setMembers(membersData);
        setMemberLookup(memberLookupObj);
        
        setTeamspaceMemberRateLookup(createRateLookupMap(membersData));
        
        setTags(tagsData);
        setTagLookup(tagLookupObj);
        
        setClients(clientsData);
        setClientLookup(clientLookupObj);

        // 6. Populate per-teamspace options cache so time entry forms use it (no refetch on open)
        setOptionsByTeamspace(prev => ({
          ...prev,
          [selectedTeamspaceId]: {
            projects: projectsData,
            members: membersData,
            tags: tagsData,
            categories: categoriesData,
            projectLookup: projectLookupObj,
            memberLookup: memberLookupObj,
            tagLookup: tagLookupObj,
            fetchedAt: Date.now(),
            loading: false,
            error: null,
          },
        }));
  
        console.log(`✅ Robust data load for teamspace ${selectedTeamspaceId} complete.`);
  
      } catch (error) {
        if (error.name !== 'CanceledError' && !controller.signal.aborted) {
          console.error('❌ Error fetching teamspace data:', error);
          setOptionsByTeamspace(prev => ({
            ...prev,
            [selectedTeamspaceId]: {
              projects: [], members: [], tags: [], categories: [],
              projectLookup: {}, memberLookup: {}, tagLookup: {},
              fetchedAt: null, loading: false, error: error.message,
            },
          }));
        }
      } finally {
        if (!controller.signal.aborted) {
          setLookupLoading(false);
        }
      }
    };
  
    fetchTeamspaceData();
  
    // 6. CLEANUP: This is the magic line that prevents the "bugging out"
    // It cancels the previous request immediately if selectedTeamspaceId changes.
    return () => controller.abort();
  
  }, [selectedTeamspaceId, dataRefreshTrigger]);

  // Add refresh function
  const refreshData = useCallback(() => {
    setDataRefreshTrigger(prev => prev + 1);
  }, []);

  /**
   * Targeted, cache-bypassing refetch of just clients + projects for the current
   * teamspace. Used by the Clients/Projects live-refresh hook: refreshData() goes
   * through the 5-minute request cache and also refetches members/tags/categories,
   * so it is both stale-prone and too broad for a per-mutation WebSocket handler.
   */
  const refreshClientsAndProjects = useCallback(async () => {
    if (!selectedTeamspaceId) return;
    try {
      const [projectsData, clientsData] = await Promise.all([
        fetchProjectsForAutocomplete(selectedTeamspaceId, true, { forceRefresh: true }),
        fetchClientsForAutocomplete(selectedTeamspaceId, { forceRefresh: true }),
      ]);

      const projectLookupObj = {};
      projectsData.forEach(p => { projectLookupObj[p.id] = p.full_project_name || p.name || ''; });
      const clientLookupObj = {};
      clientsData.forEach(c => { clientLookupObj[c.id] = c.code || c.name || ''; });

      setProjects(projectsData);
      setProjectLookup(projectLookupObj);
      setClients(clientsData);
      setClientLookup(clientLookupObj);

      // Keep the time-entry form options cache in step with the fresh projects.
      setOptionsByTeamspace(prev => {
        const existing = prev[selectedTeamspaceId];
        if (!existing) return prev;
        return {
          ...prev,
          [selectedTeamspaceId]: {
            ...existing,
            projects: projectsData,
            projectLookup: projectLookupObj,
          },
        };
      });
    } catch (error) {
      console.error('Failed to live-refresh clients/projects:', error);
    }
  }, [selectedTeamspaceId]);

  /** Fetch and cache options for a teamspace (used when form uses a different teamspace than header). Current teamspace is already loaded by the main effect. */
  const ensureOptionsForTeamspace = useCallback((teamspaceId) => {
    if (!teamspaceId) return;
    if (teamspaceId === selectedTeamspaceId) return; // main effect handles current teamspace
    setOptionsByTeamspace(prev => {
      const existing = prev[teamspaceId];
      if (existing && !existing.loading && existing.fetchedAt) return prev;
      if (existing?.loading) return prev;
      return {
        ...prev,
        [teamspaceId]: {
          projects: [], members: [], tags: [], categories: [],
          projectLookup: {}, memberLookup: {}, tagLookup: {},
          loading: true, error: null, fetchedAt: null,
        },
      };
    });
    if (optionsFetchInFlightRef.current.has(teamspaceId)) return;
    optionsFetchInFlightRef.current.add(teamspaceId);
    (async () => {
      try {
        const [projectsData, membersData, tagsData, categoriesData] = await Promise.all([
          fetchProjectsForAutocomplete(teamspaceId, true),
          fetchMembersForAutocomplete(teamspaceId),
          fetchTagsForAutocomplete(teamspaceId),
          fetchCategoriesForAutocomplete(teamspaceId),
        ]);
        const projectLookupObj = {};
        projectsData.forEach(p => { projectLookupObj[p.id] = p.full_project_name || p.name || ''; });
        const memberLookupObj = {};
        membersData.forEach(m => { memberLookupObj[m.id] = m.label; });
        const tagLookupObj = {};
        tagsData.forEach(t => { tagLookupObj[t.id] = t.label || t.name || ''; });
        setOptionsByTeamspace(prev => ({
          ...prev,
          [teamspaceId]: {
            projects: projectsData,
            members: membersData,
            tags: tagsData,
            categories: categoriesData,
            projectLookup: projectLookupObj,
            memberLookup: memberLookupObj,
            tagLookup: tagLookupObj,
            fetchedAt: Date.now(),
            loading: false,
            error: null,
          },
        }));
      } catch (err) {
        setOptionsByTeamspace(prev => ({
          ...prev,
          [teamspaceId]: {
            projects: [], members: [], tags: [], categories: [],
            projectLookup: {}, memberLookup: {}, tagLookup: {},
            fetchedAt: null, loading: false, error: err?.message || 'Failed to load',
          },
        }));
      } finally {
        optionsFetchInFlightRef.current.delete(teamspaceId);
      }
    })();
  }, [selectedTeamspaceId]);

  /** Force refetch options for a teamspace (e.g. after teamspace change in form or to get latest data). */
  const refetchOptionsForTeamspace = useCallback((teamspaceId) => {
    if (!teamspaceId) return;
    optionsFetchInFlightRef.current.delete(teamspaceId);
    setOptionsByTeamspace(prev => {
      const next = { ...prev };
      delete next[teamspaceId];
      return next;
    });
    ensureOptionsForTeamspace(teamspaceId);
  }, [ensureOptionsForTeamspace]);

  /** Revalidate in background: fetch fresh options and update cache without clearing (stale-while-revalidate). Keeps showing current data until new data arrives. */
  const revalidateOptionsForTeamspace = useCallback((teamspaceId) => {
    if (!teamspaceId) return;
    if (optionsFetchInFlightRef.current.has(teamspaceId)) return;
    optionsFetchInFlightRef.current.add(teamspaceId);
    (async () => {
      try {
        const [projectsData, membersData, tagsData, categoriesData] = await Promise.all([
          fetchProjectsForAutocomplete(teamspaceId, true),
          fetchMembersForAutocomplete(teamspaceId),
          fetchTagsForAutocomplete(teamspaceId),
          fetchCategoriesForAutocomplete(teamspaceId),
        ]);
        const projectLookupObj = {};
        projectsData.forEach(p => { projectLookupObj[p.id] = p.full_project_name || p.name || ''; });
        const memberLookupObj = {};
        membersData.forEach(m => { memberLookupObj[m.id] = m.label; });
        const tagLookupObj = {};
        tagsData.forEach(t => { tagLookupObj[t.id] = t.label || t.name || ''; });
        setOptionsByTeamspace(prev => ({
          ...prev,
          [teamspaceId]: {
            projects: projectsData,
            members: membersData,
            tags: tagsData,
            categories: categoriesData,
            projectLookup: projectLookupObj,
            memberLookup: memberLookupObj,
            tagLookup: tagLookupObj,
            fetchedAt: Date.now(),
            loading: false,
            error: null,
          },
        }));
      } catch (err) {
        // Leave existing cache as-is on revalidate failure
      } finally {
        optionsFetchInFlightRef.current.delete(teamspaceId);
      }
    })();
  }, []);

  // Time entries are now fetched independently by pages that need them
  // This function is kept for compatibility but does nothing
  const refreshTimeEntries = useCallback(async () => {
    console.log('🔄 [TeamspaceProvider] refreshTimeEntries called (handled by page)');
  }, []);

  const optimisticUpdateTimeEntry = useCallback(async (entryId, updatedData) => {
    // Optimistic updates are now handled by individual pages (e.g., CalendarPage)
    // This function is kept for compatibility but does nothing
    // Pages that need optimistic updates should manage their own state
    console.log('📝 [TeamspaceProvider] Optimistic update called (handled by page):', entryId, updatedData);
  }, []);

  const optimisticCreateTimeEntry = useCallback(async (newEntryData) => {
    // Make the actual API call to create the time entry
    console.log('➕ [TeamspaceProvider] Creating time entry via API:', newEntryData);
    
    try {
      const teamspaceId = newEntryData.teamspace;
      if (!teamspaceId) {
        throw new Error('Teamspace ID is required to create time entry');
      }
      
      const apiUrl = `${TEAMSPACE_API_URL}${teamspaceId}/time_entries/`;
      const response = await axios.post(apiUrl, newEntryData);
      
      if (response.status === 201) {
        console.log('✅ [TeamspaceProvider] Time entry created successfully:', response.data);
        // Return the created entry data (which includes the ID from the server)
        return response.data.time_entry || response.data;
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ [TeamspaceProvider] Error creating time entry:', error);
      console.error('Error response:', error.response?.data);
      // Re-throw the error so the caller can handle it
      throw error;
    }
  }, []);

  const optimisticDeleteTimeEntry = useCallback(async (entryId) => {
    // Optimistic deletes are now handled by individual pages (e.g., CalendarPage)
    // This function is kept for compatibility but does nothing
    // Pages that need optimistic deletes should manage their own state
    console.log('🗑️ [TeamspaceProvider] Optimistic delete called (handled by page):', entryId);
  }, []);

  // Memoized so useTeamspace() consumers re-render only when something they can
  // read actually changes. The provider itself re-renders on every route change
  // (useLocation) and auth refresh; a fresh object literal here used to re-render
  // every consumer -- the whole Calendar included -- each time. Every function in
  // it must be a useCallback / useMemo, or this memo never hits.
  const value = useMemo(() => ({
    teamspaces,
    loading,
    selectedTeamspaceId,
    setSelectedTeamspaceId,
    projectLookup,
    projects,
    members, // Export raw members data
    memberLookup,
    tagLookup,
    clientLookup,
    teamspaceMemberRateLookup, // Export new lookup
    lookupLoading,
    teamspaceIdToNameMap,
    currentUserTimezone, // Export current user's timezone
    currentTeamspaceTimezone: currentUserTimezone,
    displayTimezone, // Export global display timezone (calculated based on use_device_timezone preference)
    timezoneReady, // Export flag indicating if timezone is ready to use
    deviceTimezone, // Export device timezone
    clients, // Export raw clients data
    timeEntries, // Export filtered time entries for current teamspace (for compatibility)
    timeEntriesLoading, // Export loading state for time entries
    tags, // Export raw tags data
    // notionProjects, // Export Notion projects data
    // notionClients, // Export Notion clients data
    userPreferences, // Export user preferences including default_billable
    refreshUserPreferences: fetchUserPreferences, // Export function to refresh user preferences
    refreshData, // Add this
    refreshClientsAndProjects, // cache-bypassing clients+projects-only refetch (live refresh)
    refreshTimeEntries, // Add this
    optimisticUpdateTimeEntry,
    optimisticCreateTimeEntry,
    optimisticDeleteTimeEntry,
    // Global time entry mutation loading helpers
    timeEntryMutationLoading: timeEntryMutationCount > 0,
    startTimeEntryMutation,
    endTimeEntryMutation,
    calculatePeriodRanges, // Export calculatePeriodRanges
    // Per-teamspace options cache for time entry forms (avoids refetch on every form open)
    optionsByTeamspace,
    ensureOptionsForTeamspace,
    refetchOptionsForTeamspace,
    revalidateOptionsForTeamspace,
  }), [
    teamspaces,
    loading,
    selectedTeamspaceId,
    setSelectedTeamspaceId,
    projectLookup,
    projects,
    members,
    memberLookup,
    tagLookup,
    clientLookup,
    teamspaceMemberRateLookup,
    lookupLoading,
    teamspaceIdToNameMap,
    currentUserTimezone,
    displayTimezone,
    timezoneReady,
    deviceTimezone,
    clients,
    timeEntries,
    timeEntriesLoading,
    tags,
    userPreferences,
    fetchUserPreferences,
    refreshData,
    refreshClientsAndProjects,
    refreshTimeEntries,
    optimisticUpdateTimeEntry,
    optimisticCreateTimeEntry,
    optimisticDeleteTimeEntry,
    timeEntryMutationCount,
    startTimeEntryMutation,
    endTimeEntryMutation,
    calculatePeriodRanges,
    optionsByTeamspace,
    ensureOptionsForTeamspace,
    refetchOptionsForTeamspace,
    revalidateOptionsForTeamspace,
  ]);

  return (
    <TeamspaceContext.Provider value={value}>
      {children}
    </TeamspaceContext.Provider>
  );
};

export const useTeamspace = () => useContext(TeamspaceContext);

/** Hook for time entry forms: returns cached projects, members, tags, categories for a teamspace.
 * When options are loaded: (1) When the header teamspace is set, options load in the background and are cached.
 * (2) When a form opens with the same teamspace, options are already in cache (instant). (3) When the user changes teamspace inside the form, options for the new teamspace are loaded from cache or fetched once and cached.
 * When options are refreshed: (1) Changing the header teamspace refetches and updates the cache for that teamspace. (2) Call refreshData() to refetch for the current header teamspace. (3) Call the returned refetch() to refetch for the form’s teamspace (e.g. after changing teamspace in the form to always get latest). (4) Stale-while-revalidate: if cached options are older than 5 minutes, a background revalidate runs so new projects/members/tags appear without a full page refresh. */
export const useOptionsForTeamspace = (teamspaceId) => {
  const ctx = useContext(TeamspaceContext);
  const {
    optionsByTeamspace,
    ensureOptionsForTeamspace,
    refetchOptionsForTeamspace,
    revalidateOptionsForTeamspace,
    selectedTeamspaceId,
    projects,
    members,
    tags,
    projectLookup,
    memberLookup,
    tagLookup,
    lookupLoading,
  } = ctx;

  useEffect(() => {
    if (teamspaceId) ensureOptionsForTeamspace(teamspaceId);
  }, [teamspaceId, ensureOptionsForTeamspace]);

  const cached = teamspaceId ? optionsByTeamspace[teamspaceId] : null;
  const fetchedAt = cached?.fetchedAt;

  // Stale-while-revalidate: if cache is older than 5 min, refetch in background so new projects/members/tags appear
  useEffect(() => {
    if (!teamspaceId || !fetchedAt) return;
    if (Date.now() - fetchedAt <= OPTIONS_CACHE_STALE_MS) return;
    revalidateOptionsForTeamspace(teamspaceId);
  }, [teamspaceId, fetchedAt, revalidateOptionsForTeamspace]);

  const isCurrentTeamspace = teamspaceId === selectedTeamspaceId;

  if (isCurrentTeamspace && !cached) {
    const allMembers = members || [];
    const activeMembers = allMembers.filter(m => m.is_active !== false);
    const activeMemberLookup = {};
    activeMembers.forEach(m => { activeMemberLookup[m.id] = m.label; });
    return {
      projects: projects || [],
      members: activeMembers,
      allMembers,
      tags: tags || [],
      categories: [],
      projectLookup: projectLookup || {},
      memberLookup: activeMemberLookup,
      tagLookup: tagLookup || {},
      loading: lookupLoading,
      error: null,
      refetch: () => teamspaceId && refetchOptionsForTeamspace(teamspaceId),
    };
  }
  if (!cached) {
    return {
      projects: [],
      members: [],
      allMembers: [],
      tags: [],
      categories: [],
      projectLookup: {},
      memberLookup: {},
      tagLookup: {},
      loading: true,
      error: null,
      refetch: () => teamspaceId && refetchOptionsForTeamspace(teamspaceId),
    };
  }
  const allMembers = cached.members || [];
  const activeMembers = allMembers.filter(m => m.is_active !== false);
  const activeMemberLookup = {};
  activeMembers.forEach(m => { activeMemberLookup[m.id] = m.label; });
  return {
    ...cached,
    members: activeMembers,
    allMembers,
    memberLookup: activeMemberLookup,
    refetch: () => refetchOptionsForTeamspace(teamspaceId),
  };
};
